import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db, logEvent, analytics } from '../firebase.js';
import { useLocale } from '../hooks/useLocale.jsx';
import { GioService, localDayKey, newClientId, PAGE } from '../services/gio-service.js';
import { AIService } from '../services/ai-service.js';
import { CameraService } from '../services/camera.js';
import { DesignService } from '../services/design-service.js';
import { useCredits, useCreditConfig } from '../services/credits-service.js';
import { gridImage, fallbackToFull } from '../utils/design-image.js';
import { styleThumb, styleThumbFallback } from '../utils/style-thumb.js';

// /gio — 디자이너 탭 (2.3.0). 서버는 functions/gio.js, 설계는 GIO.md.
//
// 저장은 하루 한 대화(drape 스타일리스트와 같다)지만 화면은 날짜를 넘어 이어진다 —
// 최근 30개부터, 위로 올리면 더, 오늘이 끝나면 어제가 날짜 구분선과 함께 붙는다.
// 서버도 자정 직후엔 어제 대화 끝을 기억으로 붙인다. 첫 인사는 앱의 고정 문구라
// 서버를 부르지 않는다 = 무료. 메시지는 price.chat, 사진·디자인이 붙으면 price.chatPhoto
// — 2026-10-28 부터 둘 다 0 (대화 무료, 서버가 하루 chatDailyCap 통으로 막는다).
// Gio 의 답에는 실행 카드(만들기 / 수정하기)가 붙을 수 있고, 누르면 서버 작업
// (design-jobs.js) 이 돌고 결과 카드가 이 대화에 들어온다. 화면을 떠나 있으면 푸시.
const MAX_PHOTOS = 3;
// 서버 오류 코드 → 문구. 다시 해도 안 되는 것에 "다시 시도해 주세요" 를 띄우지 않는다.
const ERR_KEYS = {
  ALREADY_STARTING: 'gioAlreadyStarting',
  PHOTOS_REQUIRED: 'gioErrPhotos',
  INVALID_CUSTOM_COMMAND: 'gioErrPrompt',
  SOURCE_NOT_FOUND: 'gioErrGone',
  ACTION_NOT_FOUND: 'gioErrGone',
  CHAT_DAILY_LIMIT: 'gioDailyLimit',
};
const CHIPS = [
  { key: 'gioChipTaste', kind: 'send' },
  { key: 'gioChipTrend', kind: 'send' },
  { key: 'gioChipPhoto', kind: 'photo' },
  { key: 'gioChipMine', kind: 'design' },
];

function Monogram({ size = 'md' }) {
  return <span className={`gio-monogram gio-monogram--${size}`} aria-hidden="true">G</span>;
}

// 실행 카드가 시작시킨 작업 — 디자인 문서를 지켜보다가 끝나면 결과를 그린다.
function JobCard({ designId, jobType }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);
  useEffect(() => onSnapshot(doc(db, 'designs', designId), (s) => setD(s.exists() ? { id: s.id, ...s.data() } : null), () => setD(null)), [designId]);
  if (d === undefined) return null;
  if (!d) return <div className="schat-bubble gio-job"><p className="gio-job-note">{t('gioJobGone')}</p></div>;
  if (d.status === 'generating') {
    return (
      <div className="schat-bubble gio-job gio-job--running" aria-live="polite">
        <span className="gio-job-spin" aria-hidden="true" />
        <div>
          <strong>{t(jobType === 'edit' ? 'gioJobEditing' : 'gioJobMaking')}</strong>
          <p className="gio-job-note">{t('gioJobNotify')}</p>
        </div>
      </div>
    );
  }
  if (d.status !== 'success') {
    return (
      <div className="schat-bubble gio-job">
        <strong>{t(d.status === 'invalid' ? 'gioJobInvalid' : 'gioJobFailed')}</strong>
        <p className="gio-job-note">{t('gioJobRefunded')}</p>
      </div>
    );
  }
  const img = gridImage(d);
  return (
    <button type="button" className="schat-bubble gio-job gio-job--done" onClick={() => navigate(`/designs/${d.id}`)}>
      {img && <img src={img} alt="" loading="lazy" onError={fallbackToFull(d)} />}
      <span className="gio-job-donerow">
        <strong>{t('gioJobDone')}</strong>
        <i className="material-symbols-rounded">arrow_forward</i>
      </span>
    </button>
  );
}

// 사용자의 지난 디자인 고르기 ("불러오기"). 더 보기 커서로 끝까지 — limit 만
// 두면 n+1 번째부터 말없이 사라진다 (CLAUDE.md 목록 규칙).
function DesignPicker({ uid, onPick, onClose, title, onUpload }) {
  const { t } = useLocale();
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async (after = null) => {
    setBusy(true);
    try {
      const r = await DesignService.getUserDesigns(uid, { lastDoc: after, pageSize: 18 });
      setItems((prev) => [...(after ? prev || [] : []), ...r.designs]);
      setCursor(r.lastVisible);
      setHasMore(r.hasMore);
    } catch { setItems((prev) => prev || []); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="gio-sheet-backdrop" onClick={onClose}>
      <div className="gio-sheet" role="dialog" aria-modal="true" aria-label={title || t('gioPickDesign')} onClick={(e) => e.stopPropagation()}>
        <header className="gio-sheet-head">
          <strong>{title || t('gioPickDesign')}</strong>
          <button type="button" className="gallery-close" onClick={onClose} aria-label={t('close')}>
            <i className="material-symbols-rounded">close</i>
          </button>
        </header>
        {items && items.length === 0 && !onUpload && <p className="gio-sheet-empty">{t('gioPickEmpty')}</p>}
        <div className="gio-sheet-grid">
          {onUpload && (
            <button type="button" className="gio-sheet-cell gio-sheet-upload" onClick={onUpload}>
              <i className="material-symbols-rounded">add_photo_alternate</i>
              <span>{t('gioPickUpload')}</span>
            </button>
          )}
          {(items || []).map((d) => (
            <button key={d.id} type="button" className="gio-sheet-cell" onClick={() => onPick(d)}>
              <img src={gridImage(d)} alt={d.name || ''} loading="lazy" onError={fallbackToFull(d)} />
            </button>
          ))}
        </div>
        {hasMore && (
          <button type="button" className="btn btn-secondary gio-sheet-more" disabled={busy} onClick={() => load(cursor)}>
            {t('loadMore')}
          </button>
        )}
      </div>
    </div>
  );
}

export function Gio({ user, onSignInRequest, onOutOfCredits }) {
  const { t, lang, styleName } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const credits = useCredits();
  const cfg = useCreditConfig();
  const signedIn = user && !user.isAnonymous;
  const uid = signedIn ? user.uid : null;

  const [today, setToday] = useState(localDayKey);
  const [messages, setMessages] = useState(null);  // 오늘 (실시간)
  const [todayLimit, setTodayLimit] = useState(PAGE);
  const [todayMore, setTodayMore] = useState(false);
  const [older, setOlder] = useState([]);          // 지난 날들, 최근 날부터 [{ day, messages, hasMore, cursor }]
  const [olderDone, setOlderDone] = useState(false);
  const [pending, setPending] = useState(null);    // { id, text, previews, designThumb, sentAt, done? }
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);        // [{ blob, previewUrl }]
  const [attached, setAttached] = useState(null);  // design doc
  const [pickerOpen, setPickerOpen] = useState(false);
  const [err, setErr] = useState('');
  const [running, setRunning] = useState({});      // actionId → true
  // 만들기 카드에서 바꾼 것 (actionId →). 사진: { urls, previews, from: 'design'|'upload', date, same }
  const [choice, setChoice] = useState({});
  const [notes, setNotes] = useState({});          // 고친 추가 요청
  const [noteOpen, setNoteOpen] = useState({});
  const [photoPick, setPhotoPick] = useState(null); // { m, a } — 사진 고르는 시트
  const fileRef = useRef(null);
  const actionFileRef = useRef(null);
  const actionForPhoto = useRef(null);
  const failedDraft = useRef(null);  // { id, text } — 응답 없이 끊긴 메시지. 다시 보내면 같은 id
  const previewUrls = useRef(new Set());
  const inputRef = useRef(null);
  useEffect(() => () => previewUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  // 디자인 상세의 "Gio 에게 물어보기" — 그 디자인을 첨부한 채로 연다.
  const [attachFrom] = useState(() => location.state?.attachDesignId || null);
  useEffect(() => {
    if (!attachFrom || !uid) return;
    DesignService.getDesign(attachFrom).then((d) => { if (d && d.userId === uid) setAttached(d); }).catch(() => {});
    navigate(location.pathname, { replace: true, state: {} });
  }, [attachFrom, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // 사람이 바뀌거나 날이 바뀌면 처음부터 (어제 대화는 위로 올리면 다시 붙는다).
  useEffect(() => {
    setMessages(null);
    setTodayLimit(PAGE);
    setOlder([]);
    setOlderDone(false);
  }, [uid, today]);
  useEffect(() => {
    if (!uid) return undefined;
    return GioService.subscribeToday(uid, today, todayLimit, (msgs, more) => { setMessages(msgs); setTodayMore(more); });
  }, [uid, today, todayLimit]);

  // 화면을 켜 둔 채 자정을 넘기면 새 날로. 이 기기의 날짜가 실제로 바뀔 때만 —
  // 서버가 다른 날(users.timezone 이 아직 예전 시간대)을 돌려줘 옮겨 간 걸 1분 뒤에
  // 다시 되돌려서, 방금 대화가 사라졌다 나타났다 했다.
  useEffect(() => {
    let last = localDayKey();
    const iv = setInterval(() => {
      const k = localDayKey();
      if (k !== last) { last = k; setToday(k); }
    }, 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // 보고 있는 동안은 답장·완료 푸시를 보내지 않게 서버에 알린다 (45초 신뢰, 30초 갱신).
  useEffect(() => {
    if (!uid) return undefined;
    const ping = () => GioService.setViewing(uid, document.visibilityState === 'visible' ? today : null);
    const leave = () => GioService.setViewing(uid, null);
    ping();
    const timer = setInterval(ping, 30 * 1000);
    document.addEventListener('visibilitychange', ping);
    window.addEventListener('pagehide', leave);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', ping);
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [uid, today]);

  // 스크롤 — drape 스타일리스트(StylistChat.jsx)와 같은 방식.
  //  - 열 때, 새 메시지가 오거나 내가 보낼 때는 무조건 대화 끝으로 간다.
  //  - 위에 지난 대화가 붙을 때는 보던 메시지를 제자리에 둔다 (끝을 보고 있었으면 끝).
  //  - Gio 만의 것: 디자인 사진은 늦게 로드되며 길어진다 (drape 는 칸 크기가 고정이다).
  //    끝을 보고 있으면 목록·입력줄 높이가 바뀔 때 다시 끝으로.
  // 끝 = 문서 끝. 페이지 아래 여백이 입력줄의 sticky 간격과 같아서 마지막 메시지가
  // 입력줄 바로 위에 온다 (main.css .gio-page). 대화가 짧으면 문서가 화면보다 길지 않아
  // 움직이지 않는다.
  const listRef = useRef(null);
  const topRef = useRef(null);
  const composerRef = useRef(null);
  const settled = useRef(false);
  const atBottom = useRef(true);
  const smoothUntil = useRef(0); // 부드럽게 내려가는 중 — 그 사이 scroll 이벤트로 atBottom 을 풀지 않는다
  const anchor = useRef(null);   // { el, top } — 위에 붙기 전에 보던 메시지
  const loadingToday = useRef(false); // 오늘 한도를 늘리고 스냅샷을 기다리는 중
  const loadingOlder = useRef(false); // 지난 날을 읽는 중
  const toBottom = (smooth) => {
    const se = document.scrollingElement || document.documentElement;
    if (smooth) smoothUntil.current = Date.now() + 700;
    window.scrollTo({ top: se.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };
  const keepPlace = () => {
    const head = document.querySelector('.gio-head')?.getBoundingClientRect().bottom || 0;
    const el = [...(listRef.current?.querySelectorAll('.schat-msg, .schat-divider') || [])]
      .find((n) => n.getBoundingClientRect().top >= head);
    anchor.current = el ? { el, top: el.getBoundingClientRect().top } : null;
  };
  const lastId = messages?.[messages.length - 1]?.id;
  useEffect(() => { settled.current = false; }, [uid, today]);
  useLayoutEffect(() => {
    const a = anchor.current;
    if (!a) return;
    anchor.current = null;
    if (atBottom.current) toBottom(false);
    else window.scrollBy(0, a.el.getBoundingClientRect().top - a.top);
  }, [messages, older]);
  const seenLast = useRef(null);
  useLayoutEffect(() => {
    if (!messages) return;
    atBottom.current = true;
    const last = messages[messages.length - 1];
    const fresh = settled.current && last && last.id !== seenLast.current && last.role === 'gio' && !last.kind;
    seenLast.current = last?.id;
    const el = fresh ? listRef.current?.querySelector(`[data-mid="${last.id}"]`) : null;
    const head = document.querySelector('.gio-head')?.getBoundingClientRect().bottom || 0;
    const room = (composerRef.current?.getBoundingClientRect().top || window.innerHeight) - head;
    if (el && el.getBoundingClientRect().height > room - 24) {
      // 화면보다 긴 답장(카드 여럿)은 시작부터 — 끝에 맞추면 첫 줄이 머리글 밑으로 들어갔다.
      atBottom.current = false;
      smoothUntil.current = Date.now() + 700;
      window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - head - 12, behavior: 'smooth' });
    } else {
      toBottom(settled.current);
    }
    settled.current = true;
  }, [lastId, pending?.sentAt, messages === null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    // 바닥에서 떨어졌다고 풀어 주는 건 사용자가 위로 올렸을 때만. 내가 내린 스크롤의 이벤트가
    // 결과 카드·사진이 늦게 뜬 뒤에 처리되면 "바닥에서 멀다" 로 읽혀 고정이 풀리고, 그 뒤로
    // 따라 내려가지 않아 대화가 중간에서 시작했다 (Android, 2026-10-22).
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const up = y < lastY - 1;
      lastY = y;
      if (Date.now() < smoothUntil.current) return;
      const se = document.scrollingElement || document.documentElement;
      if (se.scrollHeight - y - window.innerHeight < 80) atBottom.current = true;
      else if (up) atBottom.current = false;
    };
    const follow = () => { if (atBottom.current) toBottom(false); };
    const ro = new ResizeObserver(follow);
    ro.observe(list);
    if (composerRef.current) ro.observe(composerRef.current);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', follow); // 키보드
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', follow);
    };
  }, [signedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // 위로 더 — 오늘의 앞부분, 그다음 지난 날의 앞부분, 그다음 그 전날.
  const loadMore = async () => {
    if (loadingToday.current || loadingOlder.current || !messages || !settled.current) return;
    if (todayMore) {
      loadingToday.current = true;
      keepPlace();
      setTodayLimit((n) => n + PAGE);
      return; // 스냅샷이 오면 아래 효과가 푼다
    }
    const top = older[older.length - 1];
    if (!top?.hasMore && olderDone) return;
    loadingOlder.current = true;
    try {
      if (top?.hasMore) {
        const r = await GioService.loadEarlier(uid, top.day, top.cursor);
        keepPlace();
        setOlder((o) => o.map((seg, i) => (i === o.length - 1 ? { ...seg, ...r, messages: [...r.messages, ...seg.messages] } : seg)));
      } else {
        const day = await GioService.previousDay(uid, top?.day || today);
        if (!day) { setOlderDone(true); return; }
        const r = await GioService.loadEarlier(uid, day);
        keepPlace();
        setOlder((o) => [...o, { day, ...r }]);
      }
    } catch {
      setOlderDone(true);
    } finally {
      loadingOlder.current = false;
    }
  };
  useEffect(() => { loadingToday.current = false; }, [messages]);
  // 어제 대화를 위에 붙인 뒤 오늘이 한도를 넘으면 limitToLast 가 미끄러지며 오늘의 앞부분이
  // 말없이 빠졌다 (어제와 오늘 사이가 비었다). 지난 날이 붙어 있으면 오늘은 다 들고 있는다.
  useEffect(() => {
    if (older.length && messages && messages.length >= todayLimit) setTodayLimit((n) => n + PAGE);
  }, [older.length, messages, todayLimit]);
  // 맨 위 표지가 보이면(또는 붙인 뒤에도 아직 화면 안이면 — 대화가 짧을 때) 더 부른다.
  useEffect(() => {
    const el = topRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) loadMore(); }, { rootMargin: '600px 0px 0px 0px' });
    io.observe(el);
    if (el.getBoundingClientRect().bottom > -600) loadMore();
    return () => io.disconnect();
  }); // 매 렌더 — loadMore 가 최신 상태를 보게

  // 임시 말풍선 — 서버 사본(u_{id} / g_{id})이 오면 그 자리를 넘긴다. 예전엔 응답이 오는
  // 순간 내려서 사본이 오기까지 내 말이 잠깐 사라졌고, 글이 같은지로 짝지어 기기 시계가
  // 어긋나면 영영 안 내려가 입력이 막혔다.
  const ids = useMemo(() => new Set((messages || []).map((m) => m.id)), [messages]);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  useEffect(() => {
    if (pending && ids.has(`g_${pending.id}`)) setPending(null);
  }, [ids, pending]);
  useEffect(() => { // 응답은 왔는데 사본이 안 보이면 (리스너 오류 등) 잠시 뒤 내린다
    if (!pending?.done) return undefined;
    const id = pending.id;
    const timer = setTimeout(() => setPending((p) => (p?.id === id ? null : p)), 10 * 1000);
    return () => clearTimeout(timer);
  }, [pending?.done, pending?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // 응답 없이 끊겼던 메시지가 서버에서는 처리됐으면 되돌려 둔 입력을 치운다.
  useEffect(() => {
    const f = failedDraft.current;
    if (!f || !ids.has(`u_${f.id}`)) return;
    failedDraft.current = null;
    setText((cur) => (cur.trim() === f.text ? '' : cur));
    setPhotos([]);
    setAttached(null);
    setErr('');
  }, [ids]);
  // Android 는 키보드가 창을 줄여서 떠 있는 탭바가 키보드 위에 남고, 입력줄이 그 위에 뜬다.
  // 입력 중에는 탭바를 감춘다 (main.css body.gio-typing). iOS 는 키보드가 창을 덮어 해당 없음.
  const typingClass = (on) => {
    if (Capacitor.getPlatform() === 'android') document.body.classList.toggle('gio-typing', on);
  };
  useEffect(() => () => document.body.classList.remove('gio-typing'), []);
  // 입력창은 내용만큼 자란다 (CSS max-height 까지). field-sizing 을 모르는 iOS 용.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  if (user === undefined) {
    // 로그인 상태를 아직 모른다 — 로그인 안내를 잠깐 띄우지 않는다 (푸시로 열 때 보였다).
    return (
      <div className="gio-page">
        <header className="gio-head">
          <Monogram size="lg" />
          <div className="gio-head-meta">
            <strong>Gio</strong>
            <span>{t('gioRole')}</span>
          </div>
        </header>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="gio-page">
        <header className="gio-head">
          <Monogram size="lg" />
          <div className="gio-head-meta">
            <strong>Gio</strong>
            <span>{t('gioRole')}</span>
          </div>
        </header>
        <div className="gio-signin">
          <p>{t('gioSignInBody')}</p>
          <button type="button" className="btn btn-primary" onClick={() => onSignInRequest?.()}>{t('signIn')}</button>
        </div>
      </div>
    );
  }

  const addFiles = async (files) => {
    const list = Array.from(files || []);
    const room = MAX_PHOTOS - photos.length;
    const picked = [];
    for (const f of list.slice(0, room)) {
      try {
        CameraService.validateImageFile(f);
        const blob = await CameraService.compressImage(f);
        const previewUrl = URL.createObjectURL(blob);
        previewUrls.current.add(previewUrl);
        picked.push({ blob, previewUrl });
      } catch { /* 아래에서 알린다 */ }
    }
    if (picked.length) setPhotos((p) => [...p, ...picked].slice(0, MAX_PHOTOS));
    // 못 읽은 파일(예: Android 의 HEIC)·3장 넘는 것을 조용히 버리지 않는다.
    setErr(picked.length < list.length ? t('gioPhotoSkipped', { n: MAX_PHOTOS }) : '');
  };
  const removePhoto = (i) => setPhotos((ps) => ps.filter((p, k) => {
    if (k === i) { URL.revokeObjectURL(p.previewUrl); previewUrls.current.delete(p.previewUrl); }
    return k !== i;
  }));

  const handleErr = (e) => {
    if (e?.status === 402 || e?.code === 'INSUFFICIENT_CREDITS') {
      setErr(t('gioNoCredits'));
      onOutOfCredits?.();
      logEvent(analytics, 'credit_exhausted', { surface: 'gio' });
      return;
    }
    setErr(t(ERR_KEYS[e?.code] || 'gioError'));
  };

  const send = async (raw) => {
    const msg = (raw ?? text).trim();
    if ((!msg && !photos.length && !attached) || pending) return;
    setErr('');
    const sending = { photos, attached };
    // 응답 없이 끊긴 메시지를 그대로 다시 보내면 같은 id — 서버가 이미 끝냈으면 그 답을
    // 돌려주고 다시 차감하지 않는다.
    const id = failedDraft.current?.text === msg ? failedDraft.current.id : newClientId();
    failedDraft.current = null;
    setPending({ id, text: msg, previews: photos.map((p) => p.previewUrl), designThumb: attached ? gridImage(attached) : null, sentAt: Date.now() });
    setText('');
    setPhotos([]);
    setAttached(null);
    try {
      const images = await Promise.all(sending.photos.map((p) => AIService.uploadOriginalImage(p.blob)));
      const r = await GioService.chat({ text: msg, images, designId: sending.attached?.id || null, clientId: id });
      if (r.thread && r.thread !== today) setToday(r.thread);
      setPending((p) => (p?.id === id ? { ...p, done: true } : p));
      logEvent(analytics, 'gio_chat', { photos: images.length, design: !!sending.attached, actions: r.actions?.length || 0 });
    } catch (e) {
      setPending(null);
      // 응답을 못 받았다 — 서버는 끝냈을 수 있다. 사본이 이미 와 있으면 성공이다.
      if (!e?.status && idsRef.current.has(`u_${id}`)) return;
      if (!e?.status) failedDraft.current = { id, text: msg };
      setText(msg);
      setPhotos(sending.photos);
      setAttached(sending.attached);
      handleErr(e);
    }
  };

  const runAction = async (m, a, photoUrls = [], additionalRequest) => {
    if (running[a.id]) return;
    setErr('');
    setRunning((r) => ({ ...r, [a.id]: true }));
    try {
      const r = await GioService.runAction({ thread: m.thread, messageId: m.id, actionId: a.id, photoUrls, additionalRequest });
      // 지난 날 대화는 한 번만 읽어 둔 것이라 서버가 카드에 남긴 표시가 안 보인다 — 여기서
      // 같이 표시한다 (안 하면 버튼이 다시 살아나 눌러도 아무 일 없는 것처럼 보였다).
      if (r?.designId) {
        const mark = (x) => (x.id === m.id ? { ...x, actions: (x.actions || []).map((y) => (y.id === a.id ? { ...y, startedDesignId: r.designId } : y)) } : x);
        setOlder((o) => o.map((seg) => (seg.day === m.thread ? { ...seg, messages: seg.messages.map(mark) } : seg)));
      }
      logEvent(analytics, 'gio_action_run', { type: a.type, style: a.style || '', withPhotos: !!photoUrls.length });
    } catch (e) {
      handleErr(e);
    } finally {
      setRunning((r) => ({ ...r, [a.id]: false }));
    }
  };

  // 카드의 사진 바꾸기 — 지난 디자인의 원본 사진을 고르거나 새로 올린다. 고르기만 하고
  // 바로 만들지 않는다 (무슨 사진으로 만드는지 보고 누르게).
  const pickDesignPhoto = (target, d) => {
    setPhotoPick(null);
    const urls = (d.originalImageUrls || []).filter(Boolean).slice(0, MAX_PHOTOS);
    if (!urls.length) { setErr(t('gioErrGone')); return; }
    const date = d.timestamp?.toDate ? localDayKey(d.timestamp.toDate()) : null;
    const a = target.a;
    const same = d.style === a.style && (d.spaceCategory || 'interior') === (a.spaceCategory || 'interior')
      ? { designId: d.id, date, additionalRequest: d.additionalRequest || null } : null;
    setChoice((c) => ({ ...c, [a.id]: { urls, previews: urls, from: 'design', date, same } }));
  };
  const uploadPhotoFor = (target) => {
    setPhotoPick(null);
    actionForPhoto.current = target;
    actionFileRef.current?.click();
  };
  const onActionFiles = async (files) => {
    const target = actionForPhoto.current;
    actionForPhoto.current = null;
    if (!target) return;
    setRunning((r) => ({ ...r, [target.a.id]: true }));
    try {
      const list = Array.from(files || []);
      const blobs = [];
      for (const f of list.slice(0, MAX_PHOTOS)) {
        try { CameraService.validateImageFile(f); blobs.push(await CameraService.compressImage(f)); } catch { /* 아래에서 알린다 */ }
      }
      if (blobs.length < list.length) setErr(t('gioPhotoSkipped', { n: MAX_PHOTOS }));
      if (!blobs.length) return;
      const urls = await Promise.all(blobs.map((b) => AIService.uploadOriginalImage(b)));
      const previews = blobs.map((b) => { const u = URL.createObjectURL(b); previewUrls.current.add(u); return u; });
      setChoice((c) => ({ ...c, [target.a.id]: { urls, previews, from: 'upload', date: null, same: null } }));
    } catch (e) {
      handleErr(e); // 업로드 실패 — 예전엔 아무 말 없이 버튼만 돌아왔다
    } finally {
      setRunning((r) => ({ ...r, [target.a.id]: false }));
    }
  };

  const openInCreate = (a) => {
    logEvent(analytics, 'gio_action_open_create', { style: a.style });
    navigate('/create', {
      state: {
        spaceCategory: a.spaceCategory,
        style: a.style,
        customStyle: a.customStyle || '',
        additionalRequest: notes[a.id] ?? (a.additionalRequest || ''),
        budget: a.budget,
        ...(a.referenceUrl && { referenceImageUrl: a.referenceUrl }),
      },
    });
  };
  const paintMyself = (a) => {
    logEvent(analytics, 'gio_action_paint', {});
    navigate(`/designs/${a.designId}`, { state: { openEdit: { photoIndex: a.photoIndex, replacement: a.replacement } } });
  };

  const shortDay = (key) => {
    const [y, mo, d] = String(key).split('-').map(Number);
    return y ? new Date(y, mo - 1, d).toLocaleDateString(lang, { month: 'short', day: 'numeric' }) : '';
  };
  const dayLabel = (key) => {
    const [y, mo, d] = key.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString(lang, { month: 'long', day: 'numeric', weekday: 'short' });
  };

  // 2.3.0 의 답장엔 스타일 키가 백틱째 남아 있다 (`dark_academia`). 이름으로 보여 준다.
  const readable = (s) => String(s || '')
    .replace(/`([^`\n]+)`/g, (_, k) => { const n = styleName(k.trim()); return n !== k.trim() ? n : k; })
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (k) => styleName(k));
  const list = messages || [];
  const all = [...older.slice().reverse().flatMap((seg) => seg.messages), ...list];
  const startedIds = new Set(all.filter((m) => m.kind === 'job_started').map((m) => m.designId));
  // 크레딧은 줄어들 때만 말한다 — 매번 가격표를 달면 대화를 아끼게 된다.
  const lowCredits = credits.plan !== 'pro' && !credits.loading && credits.credits !== null && credits.credits <= cfg.price.generate;
  // 대화가 무료면 크레딧이 떨어져도 계속 말을 걸 수 있다는 걸 알린다 (만들기만 멈춘다).
  const chatFree = cfg.price.chat === 0 && cfg.price.chatPhoto === 0;
  const canSend = !pending && (text.trim() || photos.length || attached);

  const renderAction = (m, a) => {
    const busy = !!running[a.id];
    if (a.type === 'edit') {
      return (
        <div className="schat-bubble gio-card" key={a.id}>
          <div className="gio-card-top">
            {a.thumb && <img className="gio-card-thumb" src={a.thumb} alt="" loading="lazy" />}
            <div className="gio-card-meta">
              <span className="gio-card-kicker">{t('gioCardEdit')}</span>
              <strong>{readable(a.title)}</strong>
              <span className="gio-card-change">{a.change || `${a.target} → ${a.replacement}`}</span>
            </div>
          </div>
          {a.why && <p className="gio-card-why">{readable(a.why)}</p>}
          {a.startedDesignId ? null : (
            <div className="gio-card-acts">
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => runAction(m, a)}>
                {busy ? t('gioStarting') : t('gioRunEdit', { n: cfg.price.edit })}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => paintMyself(a)}>{t('gioPaintMyself')}</button>
            </div>
          )}
        </div>
      );
    }
    const isCustom = a.style === 'custom' || a.style === 'reference';
    const label = a.style === 'custom' ? t('customStylesChip') : styleName(a.style, a.spaceCategory);
    const ch = choice[a.id];
    const urls = ch?.urls || a.photoUrls || [];
    const n = urls.length;
    const thumbs = ch?.previews || urls;
    const fromDesign = ch ? ch.from === 'design' : a.photoSource?.startsWith('design:');
    const photoDate = ch ? ch.date : a.photoDate;
    const source = ch?.from === 'upload' ? t('gioCardPhotoNew')
      : fromDesign ? (photoDate ? t('gioCardPhotoFromDesign', { date: shortDay(photoDate) }) : t('gioCardPhotoFromDesignNoDate'))
      : t('gioCardPhotoFromChat');
    const note = notes[a.id] ?? (a.additionalRequest || '');
    const same = ch ? ch.same : a.sameAs;
    // 같은 사진·같은 스타일이 이미 있다 — 막지 않고 알린다. 요청까지 같으면 거의 같은 그림이
    // 나오니 다르게 만드는 길(요청 추가 · 대화 · 만들기 화면)을 안내한다.
    const identical = same && note.trim() === (same.additionalRequest || '').trim();
    const started = !!a.startedDesignId;
    return (
      <div className="schat-bubble gio-card" key={a.id}>
        <div className="gio-card-top">
          {isCustom
            ? <span className="gio-card-thumb gio-card-thumb--icon"><i className="material-symbols-rounded">edit</i></span>
            : <img className="gio-card-thumb" src={styleThumb(a.spaceCategory, a.style)} alt="" loading="lazy" onError={styleThumbFallback(a.spaceCategory, a.style)} />}
          <div className="gio-card-meta">
            <span className="gio-card-kicker">{label}</span>
            <strong>{readable(a.title)}</strong>
          </div>
        </div>
        {a.why && <p className="gio-card-why">{readable(a.why)}</p>}
        {(a.brief || a.customStyle) && <p className="gio-card-prompt">{a.brief || a.customStyle}</p>}
        {/* 만들기 화면의 "추가 요청 사항" 과 같은 칸 — 이번에 만들 디자인의 것이라 사진(예전 디자인
            출처) 위에 둔다. */}
        {!started && (noteOpen[a.id] ? (
          <textarea
            className="gio-card-note-input"
            rows={2}
            maxLength={500}
            value={note}
            placeholder={t('additionalPlaceholder', { style: label })}
            onChange={(e) => setNotes((x) => ({ ...x, [a.id]: e.target.value }))}
            autoFocus
          />
        ) : (
          <button type="button" className={`gio-card-note${note ? ' has-note' : ''}`} onClick={() => setNoteOpen((x) => ({ ...x, [a.id]: true }))}>
            {note
              ? <><span className="gio-card-note-label">{t('additionalReqLabel')}</span>{note}</>
              : t('addAdditionalRequest')}
          </button>
        ))}
        <div className="gio-card-photo">
          {n > 0 && (
            <span className="gio-card-photo-thumbs">
              {thumbs.slice(0, MAX_PHOTOS).map((u) => <img key={u} src={u} alt="" loading="lazy" />)}
            </span>
          )}
          <span className="gio-card-photo-src">{n > 0 ? source : t('gioCardNoPhoto')}</span>
          {!started && n > 0 && (
            <button type="button" className="gio-card-link" disabled={busy} onClick={() => setPhotoPick({ m, a })}>{t('gioCardChangePhoto')}</button>
          )}
        </div>
        {same && !started && (
          <div className="gio-card-same">
            <p>
              {t('gioCardSame', { style: label, date: same.date ? shortDay(same.date) : '' })}{' '}
              <button type="button" className="gio-card-link" onClick={() => navigate(`/designs/${same.designId}`)}>{t('gioCardSameView')}</button>
            </p>
            {identical && <p className="gio-card-same-hint">{t('gioCardSameHint')}</p>}
          </div>
        )}
        {!started && (
          <div className="gio-card-acts">
            {n > 0 ? (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => runAction(m, a, ch?.urls || [], notes[a.id])}>
                {busy ? t('gioStarting') : t('gioRunGenerate', { n: cfg.price.generate * n })}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => setPhotoPick({ m, a })}>
                {busy ? t('gioCardUploading') : t('gioCardAddPhoto')}
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => openInCreate(a)}>{t('gioOpenInCreate')}</button>
          </div>
        )}
      </div>
    );
  };

  const renderMessage = (m) => {
    if (m.role === 'user') {
      return (
        <div key={m.id} className="schat-msg schat-msg--user">
          <div className="schat-col schat-col--user">
            {(m.images?.length > 0) && (
              <div className="gio-msg-photos">
                {m.images.map((u, i) => (
                  <img
                    key={u}
                    src={m.imageThumbs?.[i] || u}
                    alt=""
                    loading="lazy"
                    onError={(e) => { if (e.currentTarget.src !== u) e.currentTarget.src = u; }}
                  />
                ))}
              </div>
            )}
            {m.designId && <AttachedDesign id={m.designId} />}
            {m.text && <div className="schat-bubble"><p>{m.text}</p></div>}
          </div>
        </div>
      );
    }
    if (m.kind === 'job_started' || (m.kind === 'job_result' && !startedIds.has(m.designId))) {
      return (
        <div key={m.id} className="schat-msg schat-msg--stylist gio-msg--job">
          <span className="gio-avatar-space" aria-hidden="true" />
          <div className="schat-col"><JobCard designId={m.designId} jobType={m.jobType} /></div>
        </div>
      );
    }
    if (m.kind === 'job_result') return null;
    return (
      <div key={m.id} data-mid={m.id} className="schat-msg schat-msg--stylist">
        <Monogram />
        <div className="schat-col">
          <span className="schat-name">Gio</span>
          <div className="schat-bubble"><p>{readable(m.text)}</p></div>
          {(m.actions || []).map((a) => renderAction(m, a))}
        </div>
      </div>
    );
  };

  return (
    <div className="gio-page">
      <header className="gio-head">
        <Monogram size="lg" />
        <div className="gio-head-meta">
          <strong>Gio</strong>
          <span>{t('gioRole')}</span>
        </div>
        <span className="gio-head-note">{t('gioAiNote')}</span>
      </header>

      <div className="schat-list" ref={listRef}>
        <div ref={topRef} aria-hidden="true" />
        {older.slice().reverse().map((seg) => (
          <section key={seg.day} className="schat-day">
            <p className="schat-divider"><span>{dayLabel(seg.day)}</span></p>
            {seg.messages.map(renderMessage)}
          </section>
        ))}
        {older.length > 0 && messages && <p className="schat-divider"><span>{t('gioToday')}</span></p>}
        {messages && list.length === 0 && (
          <div className="schat-msg schat-msg--stylist">
            <Monogram />
            <div className="schat-col">
              <span className="schat-name">Gio</span>
              <div className="schat-bubble"><p>{t('gioGreeting')}</p></div>
            </div>
          </div>
        )}
        {list.map(renderMessage)}
        {pending && (
          <>
            {!ids.has(`u_${pending.id}`) && (
              <div className="schat-msg schat-msg--user">
                <div className="schat-col schat-col--user">
                  {pending.previews.length > 0 && (
                    <div className="gio-msg-photos">{pending.previews.map((u) => <img key={u} src={u} alt="" />)}</div>
                  )}
                  {pending.designThumb && <div className="gio-msg-design"><img src={pending.designThumb} alt="" /></div>}
                  {pending.text && <div className="schat-bubble"><p>{pending.text}</p></div>}
                </div>
              </div>
            )}
            {!ids.has(`g_${pending.id}`) && (
              <div className="schat-msg schat-msg--stylist">
                <Monogram />
                <div className="schat-col">
                  <span className="schat-name">Gio</span>
                  <div className="schat-bubble schat-typing" aria-live="polite">
                    <span className="schat-dots"><i /><i /><i /></span>
                    <span className="schat-typinglabel">{t('gioTyping')}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <input ref={actionFileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { onActionFiles(e.target.files); e.target.value = ''; }} />

      <div className="gio-composer" ref={composerRef}>
        {err && <p className="schat-err">{err}</p>}
        {list.length === 0 && !pending && (
          <div className="schat-chips">
            {CHIPS.map((c) => (
              <button
                key={c.key}
                type="button"
                className="schat-chip"
                onClick={() => {
                  if (c.kind === 'photo') fileRef.current?.click();
                  else if (c.kind === 'design') setPickerOpen(true);
                  else send(t(c.key));
                }}
              >
                {t(c.key)}
              </button>
            ))}
          </div>
        )}
        {lowCredits && (
          <p className="schat-quota">
            {chatFree
              ? (credits.credits > 0 ? t('gioCreditsLowFree', { n: credits.credits }) : t('gioNoCreditsChatFree'))
              : (credits.credits > 0 ? t('gioCreditsLow', { n: credits.credits, c: cfg.price.chat, p: cfg.price.chatPhoto }) : t('gioNoCredits'))}
          </p>
        )}
        {(photos.length > 0 || attached) && (
          <div className="gio-attach-row">
            {photos.map((p, i) => (
              <span key={p.previewUrl} className="gio-attach">
                <img src={p.previewUrl} alt="" />
                <button type="button" aria-label={t('gioRemove')} onClick={() => removePhoto(i)}>
                  <i className="material-symbols-rounded">close</i>
                </button>
              </span>
            ))}
            {attached && (
              <span className="gio-attach gio-attach--design">
                <img src={gridImage(attached)} alt="" />
                <button type="button" aria-label={t('gioRemove')} onClick={() => setAttached(null)}>
                  <i className="material-symbols-rounded">close</i>
                </button>
              </span>
            )}
          </div>
        )}
        <form className="schat-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <button type="button" className="gio-tool" aria-label={t('gioAttachPhoto')} disabled={!!pending || photos.length >= MAX_PHOTOS} onClick={() => fileRef.current?.click()}>
            <i className="material-symbols-rounded">add_photo_alternate</i>
          </button>
          <button type="button" className="gio-tool" aria-label={t('gioPickDesign')} disabled={!!pending} onClick={() => setPickerOpen(true)}>
            <i className="material-symbols-rounded">photo_library</i>
          </button>
          {/* 보내는 중에도 잠그지 않는다 — disabled 는 포커스를 빼서 보낼 때마다 키보드가
              내려갔다. 두 번 보내기는 send() 와 canSend 가 막는다. */}
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            maxLength={1000}
            placeholder={t('gioPlaceholder')}
            onChange={(e) => { setText(e.target.value); if (err) setErr(''); }}
            onFocus={() => typingClass(true)}
            onBlur={() => typingClass(false)}
            // keyCode 229 — Safari 는 일본어 변환을 확정하는 Enter 에 isComposing 을 false 로 준다
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); send(); } }}
          />
          <button type="submit" className="schat-send" disabled={!canSend} aria-label={t('gioSend')}>
            <i className="material-symbols-rounded">arrow_upward</i>
          </button>
        </form>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {photoPick && (
        <DesignPicker
          uid={uid}
          title={t('gioPickPhoto')}
          onClose={() => setPhotoPick(null)}
          onUpload={() => uploadPhotoFor(photoPick)}
          onPick={(d) => pickDesignPhoto(photoPick, d)}
        />
      )}
      {pickerOpen && (
        <DesignPicker
          uid={uid}
          onClose={() => setPickerOpen(false)}
          onPick={(d) => { setAttached(d); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

// 사용자 메시지에 붙은 디자인 — 썸네일만, 누르면 상세.
function AttachedDesign({ id }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { DesignService.getDesign(id).then(setD).catch(() => {}); }, [id]);
  const img = useMemo(() => (d ? gridImage(d) : null), [d]);
  if (!img) return null;
  return (
    <button type="button" className="gio-msg-design" onClick={() => navigate(`/designs/${id}`)}>
      <img src={img} alt="" loading="lazy" onError={fallbackToFull(d)} />
    </button>
  );
}

export default Gio;
